/**
 * Translucent previews of every legal placement for the current tool.
 *
 * Rebuilt whenever the model or the tool changes; `hover` then refines the one
 * under the pointer. Payloads live in a side map keyed by mesh UUID so picking
 * never has to read untyped `userData`.
 */

import { Group, Mesh, type Object3D } from "three";
import {
  BRACKET_STUB,
  CFG,
  DECK_LIFT,
  KR,
  MM,
  nodeY,
  PLANK_THICKNESS,
  SKD_HOOK_LEVELS_BELOW,
  SKD_LENGTH,
  TAPP,
} from "../../data/haki.ts";
import {
  bayEdges,
  bayFrame,
  dist,
  edgeKey,
  findBays,
  findBoardSpans,
  findStairSpans,
  kXZ,
  near,
} from "../../model/geometry.ts";
import { legNodes } from "../../model/solver.ts";
import type { Beam, Leg, Point, ScaffoldModel } from "../../model/types.ts";
import { isMounted, isSmallStair } from "../../model/types.ts";
import { G } from "../geometries.ts";
import { M } from "../materials.ts";
import type { Terrain } from "../terrain.ts";
import { tubeBetween, V, yawOf } from "../threeHelpers.ts";
import type { BoardPlacement, GhostPayload, NodePayload, Pending, ToolSettings } from "./types.ts";

/** Stripes drawn on a deck ghost to show plank direction. */
const STRIPE_COUNT = 5;

/** The UTV lengths that span a full 2.0 m lift, longest first. */
const UTV_LONG_SIZES: readonly number[] = [3050, 2500];
const UTV_LONG_MIN = 2500;

/** Two directions closer than this are the same face of a standard, radians (~20°). */
const FACE_TOLERANCE = 0.35;

/** Where a bracket ghost's arm starts, clear of the standard, metres. */
const BRACKET_GHOST_START = 0.06;

export type GhostScene = {
  readonly root: Group;
  readonly pickNodes: Mesh[];
  readonly pickGhosts: Mesh[];
  readonly payloads: Map<string, GhostPayload>;
  readonly nodes: Map<string, NodePayload>;
  /** The beam being aimed, when one is pending. */
  aimGhost: Mesh | null;
  /** The standard preview that follows the pointer. */
  spirGhost: Group | null;
  /** Container the access previews are drawn into. */
  accessGhost: Group | null;
  /** The single board that follows the pointer across a span. */
  boardGhost: Mesh | null;
};

export function createGhostScene(): GhostScene {
  return {
    root: new Group(),
    pickNodes: [],
    pickGhosts: [],
    payloads: new Map(),
    nodes: new Map(),
    aimGhost: null,
    spirGhost: null,
    accessGhost: null,
    boardGhost: null,
  };
}

function reset(ghosts: GhostScene): void {
  ghosts.root.clear();
  ghosts.pickNodes.length = 0;
  ghosts.pickGhosts.length = 0;
  ghosts.payloads.clear();
  ghosts.nodes.clear();
  ghosts.aimGhost = null;
  ghosts.spirGhost = null;
  ghosts.accessGhost = null;
  ghosts.boardGhost = null;
}

function addGhost(ghosts: GhostScene, mesh: Mesh, payload: GhostPayload, container?: Object3D): void {
  ghosts.payloads.set(mesh.uuid, payload);
  ghosts.pickGhosts.push(mesh);
  (container ?? ghosts.root).add(mesh);
}

function buildCupNodes(ghosts: GhostScene, model: ScaffoldModel, pending: Pending | null): void {
  for (const leg of model.legs.values()) {
    for (const p of legNodes(leg)) {
      const isPending = pending?.leg === leg && pending.p === p;
      const node = new Mesh(G.sph, isPending ? M.nodeHot : M.node);
      node.position.set(leg.x, nodeY(p) + KR, leg.z);
      ghosts.nodes.set(node.uuid, { leg, p });
      ghosts.pickNodes.push(node);
      ghosts.root.add(node);
    }
  }
}

function buildDeckGhosts(ghosts: GhostScene, model: ScaffoldModel): void {
  const decked = new Set(
    model.decks.map(
      (d) =>
        `${d.c
          .map((c) => kXZ(c.x, c.z))
          .sort()
          .join("|")}|${d.p}`,
    ),
  );

  for (const [key, bay] of findBays(model)) {
    if (decked.has(key)) {
      continue;
    }

    const frame = bayFrame(bay.c);
    const group = new Group();
    group.position.set(
      (bay.c[0].x + bay.c[3].x) / 2,
      nodeY(bay.p) + KR + 0.06,
      (bay.c[0].z + bay.c[3].z) / 2,
    );
    group.rotation.y = frame.rotY;

    const slab = new Mesh(G.box, M.ghost);
    slab.scale.set(frame.L - 0.12, 0.05, frame.W - 0.12);
    group.add(slab);

    const stripes = new Group();
    const span = Math.min(frame.L, frame.W) * 0.8;
    for (let i = 0; i < STRIPE_COUNT; i++) {
      const stripe = new Mesh(G.box, M.nodeHot);
      stripe.scale.set(Math.max(frame.L, frame.W) * 0.7, 0.02, 0.03);
      stripe.position.set(0, 0.04, (i / (STRIPE_COUNT - 1) - 0.5) * span);
      stripes.add(stripe);
    }
    group.add(stripes);

    addGhost(ghosts, slab, { kind: "deck", bay, stripes, alongAC: false }, group);
    ghosts.root.add(group);
  }
}

/** Is this point inside a deck at level `p`? Spans under a packed bay offer nothing to lay. */
function underDeck(model: ScaffoldModel, point: Point, p: number): boolean {
  return model.decks.some((deck) => {
    if (deck.p !== p) {
      return false;
    }

    const frame = bayFrame(deck.c);
    const local = frame.toLocal(point);

    return local.x > 0 && local.x < frame.L && local.z > 0 && local.z < frame.W;
  });
}

/**
 * One slab per pair of parallel edges an ALP length apart — where a single
 * board can lie. The pointer then picks the slot across it.
 */
function buildBoardGhosts(ghosts: GhostScene, model: ScaffoldModel, settings: ToolSettings): void {
  const wid = settings.size === 170 ? 170 : 230;

  for (const span of findBoardSpans(model, wid)) {
    const mid = (span.o0 + span.o1) / 2;
    const gap = span.len * MM;
    const cx = span.lo.a.x + span.u.x * mid + (span.dir.x * gap) / 2;
    const cz = span.lo.a.z + span.u.z * mid + (span.dir.z * gap) / 2;
    if (underDeck(model, { x: cx, z: cz }, span.p)) {
      continue;
    }

    const mesh = new Mesh(G.box, M.ghost);
    mesh.scale.set(span.o1 - span.o0, 0.03, gap - 0.1);
    mesh.position.set(cx, nodeY(span.p) + KR + 0.05, cz);
    mesh.rotation.y = yawOf(span.u.x, span.u.z) - Math.PI / 2;
    addGhost(ghosts, mesh, { kind: "board", span, wid, place: null });
  }

  const preview = new Mesh(G.box, M.ghost);
  preview.visible = false;
  ghosts.boardGhost = preview;
  ghosts.root.add(preview);
}

/** Guardrails hook onto every beam edge and onto an SK bracket's arm alike. */
function buildRailGhosts(ghosts: GhostScene, model: ScaffoldModel, settings: ToolSettings): void {
  const wantRail = settings.railKind !== "toe";
  const wantToe = settings.railKind !== "skrd";

  for (const beam of bayEdges(model)) {
    const key = edgeKey(beam.a, beam.b, beam.p);
    const hasRail = model.rails.some((r) => edgeKey(r.a, r.b, r.p) === key);
    const hasToe = model.toes.some((t) => edgeKey(t.a, t.b, t.p) === key);
    if (!((wantRail && !hasRail) || (wantToe && !hasToe))) {
      continue;
    }

    const full = wantRail && !hasRail;
    const floor = nodeY(beam.p) + KR + 0.016 + 0.09;
    const dx = beam.b.x - beam.a.x;
    const dz = beam.b.z - beam.a.z;
    const length = Math.hypot(dx, dz);

    const mesh = new Mesh(G.box, M.ghost);
    mesh.scale.set(length - 0.14, full ? 1.05 : 0.2, 0.05);
    mesh.position.set((beam.a.x + beam.b.x) / 2, floor + (full ? 0.55 : 0.1), (beam.a.z + beam.b.z) / 2);
    mesh.rotation.y = yawOf(dx, dz) - Math.PI / 2;
    addGhost(ghosts, mesh, { kind: "rail", beam });
  }
}

function buildHatchGhosts(ghosts: GhostScene, model: ScaffoldModel): void {
  model.decks.forEach((deck, deckIdx) => {
    if (CFG.hatch.art[deck.len] === undefined) {
      return;
    }

    const frame = bayFrame(deck.c);
    const mesh = new Mesh(G.box, deck.hatch ? M.ghostBad : M.ghost);
    mesh.scale.set(frame.L - 0.12, 0.03, frame.W - 0.12);
    mesh.position.set(
      (deck.c[0].x + deck.c[3].x) / 2,
      nodeY(deck.p) + KR + 0.2,
      (deck.c[0].z + deck.c[3].z) / 2,
    );
    mesh.rotation.y = frame.rotY;
    addGhost(ghosts, mesh, { kind: "luke", deckIdx });
  });
}

/**
 * One slab per transom pair — both ascend directions share it.
 *
 * Every ×2000 length is offered, not just the selected one: a stair hangs
 * between two transoms a lift apart, so hovering a beam should show whichever
 * stair actually spans to the beam above it. No deck is involved.
 */
function buildUtvGhosts(ghosts: GhostScene, model: ScaffoldModel): void {
  const groups = new Map<string, ReturnType<typeof findStairSpans>>();
  const spans = UTV_LONG_SIZES.flatMap((size) => findStairSpans(model, size));

  for (const span of spans) {
    const mid = (span.o0 + span.o1) / 2;
    const bottom = { x: span.lo.a.x + span.u.x * mid, z: span.lo.a.z + span.u.z * mid };
    const top = { x: bottom.x + span.dir.x * span.len * MM, z: bottom.z + span.dir.z * span.len * MM };
    const key = `${[kXZ(bottom.x, bottom.z), kXZ(top.x, top.z)].sort().join("~")}|${span.p}`;
    const existing = groups.get(key);
    if (existing) {
      existing.push(span);
    } else {
      groups.set(key, [span]);
    }
  }

  for (const spans of groups.values()) {
    const span = spans[0];
    if (!span) {
      continue;
    }

    const mid = (span.o0 + span.o1) / 2;
    const cx = span.lo.a.x + span.u.x * mid + (span.dir.x * span.len * MM) / 2;
    const cz = span.lo.a.z + span.u.z * mid + (span.dir.z * span.len * MM) / 2;

    const mesh = new Mesh(G.box, M.ghost);
    mesh.scale.set(span.o1 - span.o0, 0.05, span.len * MM - 0.1);
    mesh.position.set(cx, (nodeY(span.p) + nodeY(span.p + 4)) / 2 + KR, cz);
    mesh.rotation.y = yawOf(span.u.x, span.u.z) - Math.PI / 2;
    addGhost(ghosts, mesh, { kind: "utv", spans, place: null });
  }
}

function buildSmallStairGhosts(ghosts: GhostScene, model: ScaffoldModel, rise: number): void {
  for (const beam of model.beams) {
    if (nodeY(beam.p) - rise * MM < -0.1) {
      continue;
    }

    const key = edgeKey(beam.a, beam.b, beam.p);
    const taken = model.stairs.some(
      (s) => isSmallStair(s) && s.small === rise && edgeKey(s.a, s.b, s.p) === key,
    );
    if (taken) {
      continue;
    }

    const dx = beam.b.x - beam.a.x;
    const dz = beam.b.z - beam.a.z;
    const mesh = new Mesh(G.box, M.ghost);
    mesh.scale.set(Math.hypot(dx, dz) - 0.14, 0.06, 0.1);
    mesh.position.set((beam.a.x + beam.b.x) / 2, nodeY(beam.p) + KR + 0.18, (beam.a.z + beam.b.z) / 2);
    mesh.rotation.y = yawOf(dx, dz) - Math.PI / 2;
    addGhost(ghosts, mesh, { kind: "utvs", beam, rise });
  }
}

/** A ladder can hang on any beam edge; the pointer picks which side it leans to. */
function buildLadderGhosts(ghosts: GhostScene, model: ScaffoldModel): void {
  for (const beam of model.beams) {
    const taken = model.ladders.some((l) => edgeKey(l.a, l.b, l.p) === edgeKey(beam.a, beam.b, beam.p));
    if (taken) {
      continue;
    }

    const dx = beam.b.x - beam.a.x;
    const dz = beam.b.z - beam.a.z;
    const mesh = new Mesh(G.box, M.ghost);
    mesh.scale.set(Math.hypot(dx, dz) - 0.14, 0.06, 0.1);
    mesh.position.set((beam.a.x + beam.b.x) / 2, nodeY(beam.p) + KR + 0.12, (beam.a.z + beam.b.z) / 2);
    mesh.rotation.y = yawOf(dx, dz) - Math.PI / 2;
    addGhost(ghosts, mesh, { kind: "ladder", beam });
  }
}

function angleBetween(a: Point, b: Point): number {
  return Math.acos(Math.max(-1, Math.min(1, a.x * b.x + a.z * b.z)));
}

/** Unit direction from `leg` toward `other`, or null when they coincide. */
function directionTo(leg: Point, other: Point): Point | null {
  const dx = other.x - leg.x;
  const dz = other.z - leg.z;
  const length = Math.hypot(dx, dz);

  return length < 1e-6 ? null : { x: dx / length, z: dz / length };
}

/**
 * The faces a bracket could point out of: along and across every beam that
 * meets the standard, so a rotated scaffold gets its own axes.
 */
function faceDirections(beams: readonly Beam[], leg: Leg): Point[] {
  const faces: Point[] = [];
  const add = (face: Point): void => {
    if (!faces.some((known) => angleBetween(known, face) < FACE_TOLERANCE)) {
      faces.push(face);
    }
  };

  for (const beam of beams) {
    const u = directionTo(leg, near(beam.a, leg) ? beam.b : beam.a);
    if (!u) {
      continue;
    }
    add(u);
    add({ x: -u.x, z: -u.z });
    add({ x: -u.z, z: u.x });
    add({ x: u.z, z: -u.x });
  }

  return faces;
}

/** The far end of an edge that starts or ends at `leg`, or null when it does neither. */
function farEnd(edge: { a: Point; b: Point }, leg: Point): Point | null {
  if (near(edge.a, leg)) {
    return edge.b;
  }
  if (near(edge.b, leg)) {
    return edge.a;
  }

  return null;
}

/** Does a beam or bracket already leave this cup group in direction `face`? */
function faceTaken(model: ScaffoldModel, leg: Leg, p: number, face: Point): boolean {
  const edges = [...model.beams.filter((b) => b.p === p), ...model.brackets.filter((k) => k.p === p)];

  return edges.some((edge) => {
    const other = farEnd(edge, leg);
    const u = other ? directionTo(leg, other) : null;

    return u !== null && angleBetween(u, face) < FACE_TOLERANCE;
  });
}

function addBracketGhost(ghosts: GhostScene, payload: GhostPayload & { kind: "bracket" }): void {
  const { a, b, p, bracketKind } = payload;
  const u = directionTo(a, b) ?? { x: 1, z: 0 };
  const yNode = nodeY(p);
  const start = V(a.x + u.x * BRACKET_GHOST_START, yNode + KR, a.z + u.z * BRACKET_GHOST_START);
  const container = new Group();
  const parts = [
    tubeBetween(start, V(b.x, yNode + KR, b.z), G.gtube, M.ghost),
    tubeBetween(V(b.x, yNode, b.z), V(b.x, yNode + BRACKET_STUB + TAPP, b.z), G.gtube, M.ghost),
  ];
  if (bracketKind === "skd") {
    const hookY = nodeY(p - SKD_HOOK_LEVELS_BELOW) + KR;
    parts.push(tubeBetween(V(b.x, yNode, b.z), start.clone().setY(hookY), G.gtube, M.ghost));
  }

  for (const mesh of parts) {
    mesh.castShadow = false;
    addGhost(ghosts, mesh, payload, container);
  }
  ghosts.root.add(container);
}

/**
 * One ghost per free face of every ground standard at each lift that has
 * beams: no beam or bracket already leaving that cup group in that direction,
 * and no standard where the bracket would end. An SKD also needs a cup group
 * to hook onto below.
 */
function buildBracketGhosts(ghosts: GhostScene, model: ScaffoldModel, settings: ToolSettings): void {
  const bracketKind = settings.bracketKind;
  const len = bracketKind === "sk" ? settings.size : SKD_LENGTH;

  for (const leg of model.legs.values()) {
    if (isMounted(leg)) {
      continue;
    }

    const beamsAtLeg = model.beams.filter((beam) => near(beam.a, leg) || near(beam.b, leg));
    const nodes = legNodes(leg);
    const levels = [...new Set(beamsAtLeg.map((beam) => beam.p))].filter((p) => nodes.includes(p));
    const faces = faceDirections(beamsAtLeg, leg);

    for (const p of levels) {
      if (bracketKind === "skd" && !nodes.includes(p - SKD_HOOK_LEVELS_BELOW)) {
        continue;
      }

      for (const face of faces) {
        const b = { x: +(leg.x + face.x * len * MM).toFixed(3), z: +(leg.z + face.z * len * MM).toFixed(3) };
        if (faceTaken(model, leg, p, face) || model.legs.has(kXZ(b.x, b.z))) {
          continue;
        }

        addBracketGhost(ghosts, { kind: "bracket", bracketKind, a: { x: leg.x, z: leg.z }, b, p, len });
      }
    }
  }
}

function buildFreeEndGhosts(ghosts: GhostScene, model: ScaffoldModel, terrain: Terrain): void {
  const seen = new Set<string>();

  for (const beam of model.beams) {
    for (const corner of [beam.a, beam.b]) {
      const key = kXZ(corner.x, corner.z);
      if (model.legs.has(key) || seen.has(`${key}|${beam.p}`)) {
        continue;
      }
      seen.add(`${key}|${beam.p}`);

      const dot = new Mesh(G.dot, M.ghost);
      dot.position.set(corner.x, terrain.resolveGy(corner.x, corner.z, nodeY(beam.p)) + 0.03, corner.z);
      addGhost(ghosts, dot, { kind: "legAt", x: corner.x, z: corner.z, p: beam.p });
    }
  }
}

export function refreshGhosts(
  ghosts: GhostScene,
  model: ScaffoldModel,
  terrain: Terrain,
  settings: ToolSettings,
  pending: Pending | null,
): void {
  reset(ghosts);
  const { tool } = settings;

  if (tool === "beam" || tool === "diag") {
    buildCupNodes(ghosts, model, pending);
  }
  if (tool === "deck") {
    if (settings.deckKind === "board") {
      buildBoardGhosts(ghosts, model, settings);
    } else {
      buildDeckGhosts(ghosts, model);
    }
  }
  if (tool === "rail") {
    buildRailGhosts(ghosts, model, settings);
  }
  if (tool === "bracket") {
    buildBracketGhosts(ghosts, model, settings);
  }
  if (tool === "access") {
    if (settings.accKind === "luke") {
      buildHatchGhosts(ghosts, model);
    } else if (settings.accKind === "ladder") {
      buildLadderGhosts(ghosts, model);
    } else if (settings.size >= UTV_LONG_MIN) {
      buildUtvGhosts(ghosts, model);
    } else {
      buildSmallStairGhosts(ghosts, model, settings.size);
    }

    ghosts.accessGhost = new Group();
    ghosts.root.add(ghosts.accessGhost);
  }
  if (tool === "spir") {
    buildFreeEndGhosts(ghosts, model, terrain);

    const preview = new Group();
    preview.add(new Mesh(G.gtube, M.ghost), new Mesh(G.dot, M.ghost));
    preview.visible = false;
    ghosts.spirGhost = preview;
    ghosts.root.add(preview);
  }
  if (pending?.kind === "beam") {
    const aim = new Mesh(G.gtube, M.ghost);
    aim.castShadow = false;
    aim.visible = false;
    ghosts.aimGhost = aim;
    ghosts.root.add(aim);
  }
}

/** Put the board preview where the pointer's slot is, red when the slot is taken. */
export function showBoardGhost(ghosts: GhostScene, place: BoardPlacement | null): void {
  const preview = ghosts.boardGhost;
  if (!preview) {
    return;
  }
  if (!place) {
    preview.visible = false;
    return;
  }

  const { a, b, p, wid } = place.board;
  const length = dist(a, b);
  preview.visible = true;
  preview.material = place.blocked ? M.ghostBad : M.ghost;
  preview.scale.set(length - 0.13, PLANK_THICKNESS, wid * MM - 0.01);
  preview.position.set((a.x + b.x) / 2, nodeY(p) + DECK_LIFT + PLANK_THICKNESS / 2, (a.z + b.z) / 2);
  preview.rotation.y = yawOf(b.x - a.x, b.z - a.z) - Math.PI / 2;
}

/** Move the standard preview to a point on a surface. */
export function showSpirGhost(ghosts: GhostScene, x: number, z: number, y0: number, length: number): void {
  const preview = ghosts.spirGhost;
  const [tube, cap] = preview?.children ?? [];
  if (!preview || !tube || !cap) {
    return;
  }

  preview.visible = true;
  tube.scale.set(1, length, 1);
  tube.position.set(x, y0 + length / 2, z);
  cap.position.set(x, y0 + 0.01, z);
}
